import { BookText, ChevronDown } from 'lucide-react';
import { type ReactNode, useId, useState } from 'react';
import {
  type CommisionType,
  STATUS_COMMISION,
} from '@/domains/mobile-web/constant/listStatusCommision.constant';
import { formatPrice } from '@/domains/mobile-web/utils/price';
import { cn } from '@/lib/utils';
import Button from '@/shared/components/ui/Button';
import { formatDateToDDMonYYYY } from '@/shared/utils/date';
import { useColorStore } from '@/store/colorStore';

export interface AccordionAffiliateItem {
  id: string;
  title: string;
  date: string;
  status: CommisionType;
  amount: number;
  transferDate?: string | null;
  bankName: string;
  accountName: string;
  accountNumber: string;
  transferAmount: number;
}

export interface AccordionAffiliateProps {
  item: AccordionAffiliateItem;
  defaultOpen?: boolean;
  onProof?: (item: AccordionAffiliateItem) => void;
  className?: string;
}

const rupiah = (value: number) => `Rp ${formatPrice(value)}`;

const maskAccountNumber = (value: string) => {
  const digits = value.replace(/\s+/g, '');
  if (digits.length <= 4) return digits;
  return `${'*'.repeat(digits.length - 4)}${digits.slice(-4)}`;
};

const Row = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className="flex items-start justify-between gap-3">
    <span className="reg12 text-text-grey">{label}</span>
    <span className="reg12 text-black text-right">{value}</span>
  </div>
);

const AccordionAffiliate = ({
  item,
  defaultOpen = false,
  onProof,
  className,
}: AccordionAffiliateProps) => {
  const [open, setOpen] = useState(defaultOpen);
  const panelId = useId();
  const { NEWCOLOR } = useColorStore();
  const status = STATUS_COMMISION[item.status];

  return (
    <article className={cn('shadowCard w-full flex flex-col', className)}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-controls={panelId}
        className="flex w-full items-start justify-between gap-3 p-3 text-left"
      >
        <div className="flex flex-col gap-1 min-w-0">
          <span className="body1bold text-black">{item.title}</span>
          <span className="reg12 text-text-grey">
            {formatDateToDDMonYYYY(item.date)}
            <span className="mx-1">·</span>
            <span style={{ color: NEWCOLOR[status.color] }}>{status.label}</span>
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="body1bold text-text-blue-100 whitespace-nowrap">
            {rupiah(item.amount)}
          </span>
          <ChevronDown
            size={20}
            aria-hidden="true"
            className={cn('text-black transition-transform', open && 'rotate-180')}
          />
        </div>
      </button>

      {open && (
        <div id={panelId} className="px-3 pb-3">
          <div className="flex flex-col gap-3 rounded-[12px] border border-blue-cs-200 p-3">
            <p className="body2bold text-black">Detail Transaksi</p>
            <Row label="ID" value={item.id} />
            <Row
              label="Tanggal Transfer"
              value={item.transferDate ? formatDateToDDMonYYYY(item.transferDate) : '-'}
            />

            <p className="body2bold text-black">Informasi Rekening Tujuan</p>
            <Row label="Bank Tujuan" value={item.bankName} />
            <Row label="Nama Rekening" value={item.accountName} />
            <Row label="Nomor Rekening" value={maskAccountNumber(item.accountNumber)} />
            <Row label="Jumlah ditransfer" value={rupiah(item.transferAmount)} />

            <Button
              type="button"
              onClick={() => onProof?.(item)}
              disabled={!onProof}
              className="btn-fill-white"
            >
              <BookText size={16} />

              <span>Lihat bukti transfer</span>
            </Button>
          </div>
        </div>
      )}
    </article>
  );
};

export default AccordionAffiliate;
